import { useEffect, useRef, useState } from 'react'
import styles from './CategoryTab.module.less'
import cls from 'classnames'
import { DASHBOARD_CONTENT_CONTAINER_ID } from '@/router/pageConst'
import { useTranslation } from 'react-i18next'
import { useMutationObserver } from 'ahooks'
import AIcon from '@/components/A/AIcon/AIcon'
import InfoDrawerBtn from '../InfoDrawerBtn/InfoDrawerBtn'
import PDFDownloadBtn from '@/components/PDFDownloadBtn/PDFDownloadBtn'
import MiddleText from '@/components/MiddleText/MiddleText'
import PPTDownloadBtn from '@/components/PPTDownloadBtn/PPTDownloadBtn'
import { Divider, Dropdown } from 'antd'
import { userService } from '@/services/userService'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import NiceModal from '@ebay/nice-modal-react'
import SharePosterModal from '@/components/Modals/SharePosterModal/SharePosterModal'
import { observer } from 'mobx-react'


interface Props {
  project: ProjectModels.ProjectDetail
  mode?: 'default' | 'professor'
  hasTranslated?: boolean
  activeTab?: CategoryTabKey
  onChange: (key: CategoryTabKey) => void
  onReturn: () => void
  onProjectUpdate?: () => void
}

export type CategoryTabKey = 'primary' | 'detail' | 'heatmap'
export const CATEGORY_TAB_ID = 'Category-tab'

function CategoryTab({ mode = 'default', ...props }: Props) {
  const [isSharePosterDebug] = useTypedLocalStorage('aigc-poster-debug', {
    defaultValue: false,
  })
  const containerElement = useRef<HTMLDivElement>(null)
  const dashboardContentElement = useRef<HTMLDivElement>(
    document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID) as HTMLDivElement
  )
  const [isSticky, setIsSticky] = useState(false)
  const [width, setWidth] = useState(0)
  const { t } = useTranslation()
  const showShareBtn = userService.userInfo?.isFrom === 'aigc'
  // const showShareBtn = true
  const tabs: Array<{ key: CategoryTabKey; title: string }> = [
    { key: 'primary', title: t('dashboard.projectDetailPage.primaryAnalysis') },
    { key: 'detail', title: t('dashboard.projectDetailPage.detailAnalysis') },
    { key: 'heatmap', title: t('dashboard.projectDetailPage.heatmapAnalysis') },
  ]
  const [offsetTop, setOffsetTop] = useState(0)
  const [tabKey, setTabKey] = useState<CategoryTabKey>('primary')
  const handleClick = (key: CategoryTabKey) => {
    setTabKey(key)
    props.onChange(key)
    dashboardContentElement.current?.scrollTo({ top: 0 })
  }

  const handleShare = () => {
    NiceModal.show(SharePosterModal, {
      project: props.project,
      userName: userService.userInfo?.name || '',
      userAvatar: userService.userInfo?.avatar,
      downloadedAt: Date.now(),
      debug: isSharePosterDebug,
    })
  }

  useEffect(() => {
    handleClick(props.activeTab || 'primary')
  }, [props.activeTab])

  useEffect(() => {
    const dashboardContentElement = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
    setOffsetTop(dashboardContentElement?.getBoundingClientRect().top || 0)

    const handleScroll = () => {
      const containerOffsetTop = containerElement.current?.getBoundingClientRect().top
      setIsSticky(containerOffsetTop! <= offsetTop)
    }

    // const dashboardContentElement = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
    if (dashboardContentElement) {
      dashboardContentElement.addEventListener('scroll', handleScroll)
    }
    return () => {
      dashboardContentElement?.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useMutationObserver(
    () => {
      const width = containerElement.current?.getBoundingClientRect().width
      setWidth(width || 0)
    },
    dashboardContentElement,
    { childList: true, subtree: true }
  )

  return (
    <div className={cls(styles.categoryTab)} id={CATEGORY_TAB_ID} ref={containerElement}>
      <div
        className={styles.categoryFloatTab}
        style={{
          position: isSticky ? 'fixed' : 'static',
          top: isSticky ? offsetTop : 'auto',
          width: isSticky ? width : '100%',
          zIndex: 100,
          boxShadow: isSticky ? '0px 4px 12px 0px rgba(51, 51, 51, 0.06)' : 'none',
        }}
      >
        {isSticky && (
          <span className={styles.leftArrowIcon} onClick={props.onReturn}>
            <AIcon type='icon-line-global_left' />
          </span>
        )}
        <ul className={cls(styles.tabs)}>
          {tabs.map((tab, index) => {
            return (
              <li
                className={cls(styles.tab, { [styles.active]: tabKey === tab.key })}
                key={index}
                onClick={() => handleClick(tab.key)}
              >
                {tab.title}
              </li>
            )
          })}
        </ul>
        <div className={styles.rightBox}>
          {mode === 'default' && (
            <>
              {showShareBtn && (
                <button
                  type='button'
                  disabled={props.project.status !== 'completed'}
                  style={{ opacity: props.project.status !== 'completed' ? 0.5 : 1 }}
                  className={cls(styles.shareBtn, { [styles.sticky]: isSticky })}
                  onClick={handleShare}
                >
                  <AIcon type='icon-fill-global_Share' width={14} />
                  <MiddleText className={styles.text} text={t('dashboard.projectDetailPage.share')} />
                </button>
              )}
              <span
                className={cls(styles.downloadBox, { [styles.sticky]: isSticky })}
                style={{ opacity: props.project.status !== 'completed' ? 0.5 : 1 }}
              >
                <PPTDownloadBtn
                  debug={false}
                  project={props.project}
                  hasTranslated={props.hasTranslated}
                  as={(p) => <DownloadBtn {...p} />}
                />
                <Divider type='vertical' style={{ marginLeft: 0, marginRight: 0 }} />
                <Dropdown placement='bottomRight' trigger={['click']} disabled={props.project.status !== 'completed'} menu={{
                  items: [{
                    label: <PDFDownloadBtn
                      debug={false}
                      project={props.project}
                      hasTranslated={props.hasTranslated}
                      as={(p) => <DropdownBtn {...p} />}
                    />,
                    key: 'pdf',
                  }
                  ]
                }}>
                  <span className={styles.downloadIconBox}>
                    <AIcon className={styles.downloadIcon} type="icon-fill-global_down" />
                  </span>
                </Dropdown>
              </span>
            </>
          )}
          {isSticky && <InfoDrawerBtn mode={mode} project={props.project} onProjectUpdate={props.onProjectUpdate} />}
        </div>
      </div>
    </div >
  )
}

function DropdownBtn(props: { loading: boolean }) {
  const { t } = useTranslation()
  return (
    <span className={styles.dropdownBtn}>
      {props.loading && (
        <span className={styles.spin}>
          <AIcon type='icon-line-global_loading' width={16} style={{ color: '#8463F9' }} />
        </span>
      )}
      {!props.loading && (
        <AIcon type={'icon-line-global_pdf'} width={18} style={{ color: '#1C1E22' }} />
      )}
      <MiddleText className={styles.text} text={t('dashboard.pdf.downloadBtnText')} />
    </span>

  )
}

function DownloadBtn(props: { loading: boolean }) {
  const { t } = useTranslation()
  return (
    <span className={styles.downloadBtn}>
      {props.loading && (
        <span className={styles.spin}>
          <AIcon type='icon-line-global_loading' width={16} style={{ color: '#8463F9' }} />
        </span>
      )}
      {!props.loading && (
        <AIcon type={'icon-line-global_download'} width={16} style={{ color: '#8463F9' }} />
      )}
      <MiddleText className={styles.text} text={t('dashboard.ppt.downloadBtnText')} />
    </span>
  )
}

export default observer(CategoryTab)
